import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '../lib/dateUtils'
import useSubmissionDetail from '../hooks/useSubmissionDetail'
import ConfirmDialog from '../components/admin/ConfirmDialog'
import { ErrorAlert } from '../components/ui'
import { db } from '../lib/supabase'
import {
  getQuestionLabel,
  getGroupInfo,
  getScaleLabel,
  getMatrixGroups,
  getProfileQuestions,
} from '../lib/questionMapper'

// Build a lookup from question_id -> answer for profile responses
function buildProfileLookup(profileResponses) {
  const map = {}
  profileResponses.forEach(r => {
    map[r.question_id] = r.answer
  })
  return map
}

// Build a lookup from question_id -> score for likert responses
function buildLikertLookup(likertResponses) {
  const map = {}
  likertResponses.forEach(r => {
    map[r.question_id] = r.score
  })
  return map
}

function RespondentHeader({ respondent, t }) {
  const { i18n } = useTranslation('survey')
  const locale = i18n.language?.startsWith('zh') ? 'zh-CN' : 'en'

  // Translate stored English sex value to current language
  const enSexOptions = i18n.t('profile.q3.options', { ns: 'survey', lng: 'en', returnObjects: true })
  const currentSexOptions = i18n.t('profile.q3.options', { ns: 'survey', returnObjects: true })
  const sexIdx = Array.isArray(enSexOptions) ? enSexOptions.indexOf(respondent.biological_sex) : -1
  const translatedSex = sexIdx >= 0 && Array.isArray(currentSexOptions) ? currentSexOptions[sexIdx] : respondent.biological_sex

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{t('submissionDetail.respondentHeader.name')}</div>
          <div className="text-gray-800 font-medium">{respondent.name}</div>
        </div>
        <div>
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{t('submissionDetail.respondentHeader.email')}</div>
          <div className="text-gray-800">{respondent.email}</div>
        </div>
        <div>
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{t('submissionDetail.respondentHeader.biologicalSex')}</div>
          <div className="text-gray-800">{translatedSex || '\u2014'}</div>
        </div>
        <div>
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{t('submissionDetail.respondentHeader.age')}</div>
          <div className="text-gray-800">{respondent.age ?? '\u2014'}</div>
        </div>
        <div>
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{t('submissionDetail.respondentHeader.partner')}</div>
          <div className="text-gray-800">{respondent.partner_name || '\u2014'}</div>
        </div>
        <div>
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{t('submissionDetail.respondentHeader.submitted')}</div>
          <div className="text-gray-800">{formatDateTime(respondent.completed_at, locale)}</div>
        </div>
      </div>
    </div>
  )
}

function ProfileSection({ profileLookup, t }) {
  const { i18n } = useTranslation('survey')
  const questions = getProfileQuestions()

  // Translate a stored English answer to current language
  function translateAnswer(q, answer) {
    if (!answer || !q.optionsKey) return answer
    const enOptions = i18n.t(q.optionsKey, { ns: 'survey', lng: 'en', returnObjects: true })
    const currentOptions = q.options
    if (!Array.isArray(enOptions) || !Array.isArray(currentOptions)) return answer

    if (q.allowMultiple) {
      const parts = answer.split(',').map(s => s.trim())
      return parts.map(part => {
        const idx = enOptions.indexOf(part)
        return idx >= 0 ? currentOptions[idx] : part
      }).join(', ')
    }

    const idx = enOptions.indexOf(answer)
    return idx >= 0 ? currentOptions[idx] : answer
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
      <h3 className="font-display text-lg font-medium text-gray-800 mb-4">{t('submissionDetail.profileResponses')}</h3>
      <div className="space-y-3">
        {questions.map(q => (
          <div key={q.id} className="flex flex-col sm:flex-row sm:gap-4 py-2 border-b border-gray-50 last:border-0">
            <div className="text-gray-500 text-sm sm:w-1/2 mb-1 sm:mb-0">{q.title}</div>
            <div className="text-gray-800 font-medium sm:w-1/2">
              {translateAnswer(q, profileLookup[q.id]) || '\u2014'}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function LikertGroupSection({ group, likertLookup, t }) {
  const groupInfo = getGroupInfo(group.id)
  if (!groupInfo) return null

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm mb-4">
      <h3 className="font-display text-lg font-medium text-gray-800 mb-4">{groupInfo.title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">{t('submissionDetail.likertTable.number')}</th>
              <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">{t('submissionDetail.likertTable.question')}</th>
              <th className="text-center py-2 px-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">{t('submissionDetail.likertTable.score')}</th>
              <th className="text-left py-2 pl-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">{t('submissionDetail.likertTable.response')}</th>
            </tr>
          </thead>
          <tbody>
            {group.questions.map((q, idx) => {
              const score = likertLookup[q.id]
              return (
                <tr key={q.id} className={`border-b border-gray-50 ${idx % 2 === 0 ? 'bg-gray-50/50' : ''}`}>
                  <td className="py-2 pr-4 text-gray-400">{idx + 1}</td>
                  <td className="py-2 pr-4 text-gray-700">{q.title}</td>
                  <td className="py-2 px-2 text-center">
                    {score != null ? (
                      <span className="inline-block w-7 h-7 leading-7 text-center bg-primary-50 text-primary-700 rounded-full text-xs font-semibold">
                        {score}
                      </span>
                    ) : '\u2014'}
                  </td>
                  <td className="py-2 pl-2 text-gray-500 text-xs">
                    {getScaleLabel(group.id, score)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function SubmissionDetail() {
  const { t } = useTranslation('admin')
  const { id } = useParams()
  const navigate = useNavigate()
  const { submission, loading, error } = useSubmissionDetail(id)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleteError, setDeleteError] = useState(null)

  const handleDelete = async () => {
    setDeleteError(null)
    try {
      await db.deleteSubmission(id)
      navigate('/admin', { replace: true })
    } catch (err) {
      setDeleteError(err.message)
      setShowDeleteDialog(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="text-gray-400 text-lg">{t('submissionDetail.loadingSubmission')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto">
        <ErrorAlert message={error} />
        <button
          onClick={() => navigate('/admin')}
          className="text-primary-600 font-semibold hover:underline"
        >
          {t('submissionDetail.backToDashboard')}
        </button>
      </div>
    )
  }

  if (!submission) return null

  const { respondent, profileResponses, likertResponses } = submission
  const profileLookup = buildProfileLookup(profileResponses)
  const likertLookup = buildLikertLookup(likertResponses)
  const matrixGroups = getMatrixGroups()

  const dciGroups = matrixGroups.filter(g => g.section === 'dyadic')
  const piGroups = matrixGroups.filter(g => g.section === 'personality')

  return (
    <div className="animate-fadeIn max-w-4xl">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/admin')}
          className="text-gray-500 hover:text-gray-700 font-semibold transition-colors"
        >
          {t('submissionDetail.backToResponses')}
        </button>
        <button
          onClick={() => setShowDeleteDialog(true)}
          className="px-4 py-2 text-sm font-semibold text-red-600 bg-red-50 rounded-lg transition-all hover:bg-red-100"
        >
          {t('submissionDetail.deleteSubmission')}
        </button>
      </div>

      <ErrorAlert message={deleteError} onDismiss={() => setDeleteError(null)} />

      {/* Respondent info */}
      <RespondentHeader respondent={respondent} t={t} />

      {/* Profile responses */}
      <ProfileSection profileLookup={profileLookup} t={t} />

      {/* DCI groups */}
      <div className="mb-6">
        <h2 className="font-display text-xl font-medium text-gray-800 mb-4">
          {t('submissionDetail.dyadicCopingInventory')}
        </h2>
        {dciGroups.map(group => (
          <LikertGroupSection
            key={group.id}
            group={group}
            likertLookup={likertLookup}
            t={t}
          />
        ))}
      </div>

      {/* Personality groups */}
      <div className="mb-6">
        <h2 className="font-display text-xl font-medium text-gray-800 mb-4">
          {t('submissionDetail.personalityInventory')}
        </h2>
        {piGroups.map(group => (
          <LikertGroupSection
            key={group.id}
            group={group}
            likertLookup={likertLookup}
            t={t}
          />
        ))}
      </div>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={showDeleteDialog}
        title={t('submissionDetail.deleteDialog.title')}
        message={t('submissionDetail.deleteDialog.message', { name: respondent.name })}
        confirmText={t('submissionDetail.deleteDialog.confirmText')}
        danger
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </div>
  )
}
